/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      if (this.scrollAffordanceHandler) {
        this.removeEventListener('scroll', this.scrollAffordanceHandler, { capture: true });
        this.removeEventListener('click', this.moreItemsClickHandler);
        window.removeEventListener('resize', this.resizeAffordanceHandler);
      }

      if (this.affordanceObserver) this.affordanceObserver.disconnect();
      cancelAnimationFrame(this.affordanceFrame);
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }

      this.initScrollAffordance();
    }

    /**
     * Keeps the "n more items" pill in sync with how many lines are still below the
     * fold, and scrolls them into view when it is tapped.
     */
    initScrollAffordance() {
      // Cart updates re-render whole blocks of the drawer, `.drawer__content`
      // among them, so every lookup happens per measurement. Holding a reference
      // leaves it detached after the first update, measuring 0x0 - which is how
      // the pill ended up counting every line, then never appearing at all.
      const scrollArea = () => this.querySelector('.drawer__content');
      if (!scrollArea()) return;

      // A line counts as "still to come" when less than half of it is on screen.
      // Counting every line the footer clips would include the one being read,
      // whose upsell card is visible but whose last few pixels are cut off.
      this.unreachedItems = () => {
        const area = scrollArea();
        if (!area) return [];

        const view = area.getBoundingClientRect();
        // A collapsed area means the markup is mid-swap; measuring against it
        // would put every line "below the fold".
        if (view.height < 1) return [];

        return [...this.querySelectorAll('.cart-item')].filter((item) => {
          const box = item.getBoundingClientRect();
          const visible = Math.min(box.bottom, view.bottom) - Math.max(box.top, view.top);
          // `box.bottom` guards against lines already scrolled past, whose
          // visible height is negative.
          return box.bottom > view.bottom && visible < box.height / 2;
        });
      };

      this.measureScrollAffordance = () => {
        const pill = this.querySelector('.js-more-items');
        if (!pill) return;

        // `open` and `aria-hidden` are set when the slide-in starts, but the
        // geometry only means anything once it has finished - measuring mid-slide
        // makes the pill blink in, out and back in again.
        if (!this.drawerSettled) {
          pill.hidden = true;
          this.classList.remove('has-more-below');
          return;
        }

        const count = this.unreachedItems().length;
        pill.hidden = count === 0;
        this.classList.toggle('has-more-below', count > 0);
        if (count === 0) return;

        const label = count === 1 ? pill.dataset.labelOne : pill.dataset.labelOther;
        pill.querySelector('.js-more-items-text').textContent = label.replace('%n', count);
      };

      // A cart update swaps several blocks in turn, so measuring straight from the
      // notification reads half-applied geometry. Coalesce to one measurement two
      // frames later, once layout has settled.
      this.scrollAffordanceHandler = () => {
        cancelAnimationFrame(this.affordanceFrame);
        this.affordanceFrame = requestAnimationFrame(() => {
          this.affordanceFrame = requestAnimationFrame(this.measureScrollAffordance);
        });
      };

      this.moreItemsClickHandler = (evt) => {
        if (!evt.target.closest('.js-more-items')) return;

        const [next] = this.unreachedItems();
        if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      // Scroll doesn't bubble, so listen in the capture phase - that way the
      // listener survives `.drawer__content` being replaced.
      this.addEventListener('scroll', this.scrollAffordanceHandler, { capture: true, passive: true });
      this.addEventListener('click', this.moreItemsClickHandler);
      this.addEventListener(`on:${this.dataset.name}:before-close`, () => {
        this.drawerSettled = false;
      });

      this.resizeAffordanceHandler = this.scrollAffordanceHandler;
      window.addEventListener('resize', this.resizeAffordanceHandler);

      // Catches every re-render: lines added or removed, and the summary being
      // rebuilt with a fresh hidden pill. Mutations from the label this writes are
      // skipped so it can't retrigger itself.
      this.affordanceObserver = new MutationObserver((records) => {
        const ownWrite = records.every((record) => {
          const el = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          return el && el.closest('.js-more-items');
        });
        if (!ownWrite) this.scrollAffordanceHandler();
      });
      this.affordanceObserver.observe(this, { childList: true, subtree: true });

      this.scrollAffordanceHandler();
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }

          // Only measurable once the drawer has actually slid into place.
          this.drawerSettled = true;
          if (this.scrollAffordanceHandler) this.scrollAffordanceHandler();
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems && typeof cartItems.refresh === 'function') {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
          CartDrawer.recalculateCssVarHeights();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
